import { useMemo, useCallback, useState } from "react";
import { message } from "antd";
import { useSelector, useDispatch } from "react-redux";
import { getBusColumns } from "./getBusColumns";
import CustomTable from "../../../components/CustomTable/CustomTable";
import {
  TABLE_COLUMN_DEFAULTS,
  buildTableScreenKey,
} from "../../../constants/tableColumnDefaults";
import CustomModal from "../../../components/CustomModal/CustomModal";
import ActionReasonModal from "../../../components/ActionReasonModal/ActionReasonModal";
import {
  BUSINESS_SUCCESS_MESSAGES,
} from "../../../constants/constants";
import { getBusinessView } from "../../../redux/slices/BusSlice";
import {
  deactivateOrg,
  deliquentBus,
  blacklistBus,
  reactivateOrg,
} from "../../../redux/slices/EnqMngtSlice";
import { useNavigate } from "react-router-dom";
import { count } from "../../../redux/slices/dealsSlice";

const BusTable = (props) => {
  const { tableData, selectedStatus, onColumnControlChange, pagination } = props;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { loading, commentSubmitError, commentSubmitLoading } = useSelector(
    (state) => state?.enqMngt,
  );
  const [enterpriseModal, setEnterpriseModal] = useState(false);
  const [enterpriseList, setEnterpriseList] = useState([]);
  const userPrivileges = useMemo(() => {
    const raw = user?.privilege || [];
    return raw
      .map((p) => (typeof p === "string" ? p : p?.privilegeCode))
      .filter(Boolean);
  }, [user]);

  const [modal, setModal] = useState({ type: null, data: null });

  const closeModal = useCallback(
    () => setModal({ type: null, data: null }),
    [],
  );

  const handleViewEnterprises = useCallback((record) => {
    setEnterpriseList(record?.entList || []);
    setEnterpriseModal(true);
  }, []);

  // Unified handler for all actions
  const openModal = useCallback((type, org) => {
    setModal({ type, data: org });
  }, []);

  const handleDeliquentClick = useCallback(
    (org) => openModal("deliquent", org),
    [openModal],
  );

  const handleDeactivateClick = useCallback(
    (org) => openModal("deactivate", org),
    [openModal],
  );

  const handleBlacklistClick = useCallback(
    (org) => openModal("blacklist", org),
    [openModal],
  );

  const handleReactivateClick = useCallback(
    (org) => openModal("activate", org),
    [openModal],
  );
  const ACTION_MAP = useMemo(
    () => ({
      deliquent: deliquentBus,
      deactivate: deactivateOrg,
      blacklist: blacklistBus,
      activate: reactivateOrg,
    }),
    [],
  );

  const handleReasonSubmit = useCallback(
    async (values) => {
      if (!modal.type || !modal.data?.orgId) return;
      const action = ACTION_MAP[modal.type];
      if (!action) return;
      const res = await dispatch(
        action({
          orgId: modal.data.orgId,
          dealCategoryId: modal.data.dealCategoryId,
          comments: values.reason,
          modifiedAt: modal.data.modifiedAt,
        }),
      );
      if (res?.payload?.statusCode === 201) {
        message.success(
          BUSINESS_SUCCESS_MESSAGES[modal.type](modal.data?.busName),
        );

        dispatch(getBusinessView(selectedStatus));
        dispatch(count("BUSINESS"));
        closeModal();
      }
    },
    [dispatch, modal, closeModal, selectedStatus, ACTION_MAP],
  );

  // memoize columns
  const columns = useMemo(
    () =>
      getBusColumns({
        onView: (org) => {
          const rowStatus =
            org?.statusId ||
            (Array.isArray(selectedStatus)
              ? selectedStatus[0]
              : selectedStatus);
          navigate(`/business/bus-details/${org.orgId}/${rowStatus}`, {
            state: org,
          });
        },
        onDeliquent: handleDeliquentClick,
        onDeactivate: handleDeactivateClick,
        onBlacklist: handleBlacklistClick,
        onActivate: handleReactivateClick,
        onViewEnterprises: handleViewEnterprises,
        status: selectedStatus,
        privileges: userPrivileges,
      }),
    [
      handleDeliquentClick,
      handleDeactivateClick,
      handleBlacklistClick,
      handleReactivateClick,
      handleViewEnterprises,
      selectedStatus,
      userPrivileges,
      navigate,
    ],
  );
  return (
    <>
      <CustomTable
        columns={columns}
        dataSource={tableData}
        loading={loading}
        screenKey={buildTableScreenKey("business")}
        defaultColumnKeys={TABLE_COLUMN_DEFAULTS.business}
        onColumnControlChange={onColumnControlChange}
        pagination={pagination}
      />
      <ActionReasonModal
        modalType={modal.type}
        onClose={closeModal}
        onSubmit={handleReasonSubmit}
        submitLoading={commentSubmitLoading}
        submitError={commentSubmitError}
      />
      <CustomModal
        title="List of Enterprises"
        open={enterpriseModal}
        onCancel={() => setEnterpriseModal(false)}
        footer={null}
        closeIcon
        width={700}
        content={
          <div
            style={{
              border: "1px solid #f0f0f0",
              borderRadius: "6px",
              overflow: "hidden",
            }}
          >
            {enterpriseList.length > 0 ? (
              enterpriseList.map((name, index) => (
                <div
                  key={index}
                  style={{
                    padding: "12px 16px",
                    borderBottom:
                      index !== enterpriseList.length - 1
                        ? "1px solid #f0f0f0"
                        : "none",
                    fontSize: "14px",
                  }}
                >
                  {index + 1}. {name}
                </div>
              ))
            ) : (
              <div style={{ padding: "16px" }}>No Enterprises Found</div>
            )}
          </div>
        }
      />
    </>
  );
};

export default BusTable;
